import type { CountryCode } from "../types/countryFields.types";
import type { FieldConfig } from "../types/formField.types";
import { BANKS_CHILE, BANKS_PERU, BANKS_ECUADOR, BANKS_PANAMA } from "./banks.config";

export const COUNTRY_FIELD_CONFIGS: Record<CountryCode, FieldConfig[]> = {
  Colombia: [
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
      maxLength: 100,
    },
    {
      name: "description",
      type: "text",
      label: "fields.description",
      placeholder: "placeholders.description",
      required: true,
      maxLength: 200,
    },
    {
      name: "key_number",
      type: "text",
      label: "fields.key_number",
      placeholder: "placeholders.key_number",
      required: true,
    },
  ],

  Brasil: [
    {
      name: "pix_type",
      type: "select",
      label: "fields.pix_type",
      required: true,
      options: [
        { value: 0, label: "CPF" },
        { value: 1, label: "CNPJ" },
        { value: 2, label: "EMAIL" },
        { value: 3, label: "Teléfono" },
        { value: 4, label: "EVP" },
      ],
      dependentFields: ["pix_key"],
    },
    {
      name: "pix_key",
      type: "text",
      label: "fields.pix_key",
      placeholder: "placeholders.pix_key",
      required: true,
      customValidator: (value, formValues) => {
        const pixType = formValues.pix_type;
        if (pixType === undefined || pixType === null) {
          return "Seleccione tipo PIX primero";
        }

        switch (pixType) {
          case 0: // CPF
            return /^\d{11}$/.test(value) ? undefined : "CPF debe tener 11 dígitos";
          case 1: // CNPJ
            return /^\d{14}$/.test(value) ? undefined : "CNPJ debe tener 14 dígitos";
          case 2: // EMAIL
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.toLowerCase())
              ? undefined
              : "Email inválido, debe estar en minúsculas";
          case 3: {
            // Phone (up to 11 digits, formatting ignored)
            const digits = value.replace(/\D/g, "");
            return digits.length >= 10 && digits.length <= 11
              ? undefined
              : "Teléfono debe tener hasta 11 dígitos";
          }
          case 4: // EVP (UUID)
            return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value)
              ? undefined
              : "UUID inválido. Formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx";
          default:
            return "Tipo PIX inválido";
        }
      },
    },
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
      maxLength: 100,
    },
    {
      name: "description",
      type: "text",
      label: "fields.description",
      placeholder: "placeholders.description",
      required: false,
      maxLength: 200,
    },
  ],

  México: [
    {
      name: "clabe",
      type: "text",
      label: "fields.clabe",
      placeholder: "placeholders.clabe_mexico",
      required: true,
      pattern: /^\d{18}$/,
      helperText: "helpers.clabe_18_digits",
      minLength: 18,
      maxLength: 18,
    },
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
      maxLength: 100,
    },
  ],

  Argentina: [
    {
      name: "protocol",
      type: "select",
      label: "fields.protocol",
      required: true,
      options: [
        { value: "cvu", label: "CVU" },
        { value: "cbu", label: "CBU" },
      ],
    },
    {
      name: "clabe",
      type: "text",
      label: "fields.account_number",
      placeholder: "placeholders.account_number",
      required: true,
      pattern: /^\d{22}$/,
      minLength: 22,
      maxLength: 22,
    },
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
      maxLength: 100,
    },
  ],

  Chile: [
    {
      name: "url",
      type: "url",
      label: "fields.webhook_url",
      placeholder: "placeholders.webhook_url",
      required: true,
      maxLength: 255,
      helperText: "helpers.webhook_url",
    },
    {
      name: "first_name",
      type: "text",
      label: "fields.first_name",
      placeholder: "placeholders.first_name",
      required: true,
    },
    {
      name: "last_name",
      type: "text",
      label: "fields.last_name",
      placeholder: "placeholders.last_name",
      required: true,
    },
    {
      name: "identification_type",
      type: "text",
      label: "fields.identification_type",
      placeholder: "placeholders.identification_type",
      required: true,
    },
    {
      name: "identification_number",
      type: "text",
      label: "fields.identification_number",
      placeholder: "placeholders.identification_number",
      required: true,
    },
    {
      name: "bank",
      type: "select",
      label: "fields.bank",
      required: true,
      options: BANKS_CHILE.map((bank) => ({ value: bank, label: bank })),
    },
    {
      name: "account_type",
      type: "select",
      label: "fields.account_type",
      required: true,
      options: [
        { value: "AHORROS", label: "AHORROS" },
        { value: "CORRIENTE", label: "CORRIENTE" },
      ],
    },
    {
      name: "account_number",
      type: "text",
      label: "fields.account_number",
      placeholder: "placeholders.account_number",
      required: true,
    },
    {
      name: "address",
      type: "text",
      label: "fields.address",
      placeholder: "placeholders.address",
      required: true,
    },
    {
      name: "city",
      type: "text",
      label: "fields.city",
      placeholder: "placeholders.city",
      required: true,
    },
    {
      name: "state",
      type: "text",
      label: "fields.state",
      placeholder: "placeholders.state",
      required: true,
    },
    {
      name: "postal_code",
      type: "text",
      label: "fields.postal_code",
      placeholder: "placeholders.postal_code",
      required: true,
    },
    {
      name: "birth_date",
      type: "date",
      label: "fields.birth_date",
      required: true,
    },
    {
      name: "email",
      type: "email",
      label: "fields.email",
      placeholder: "placeholders.email",
      required: true,
    },
    {
      name: "gender",
      type: "select",
      label: "fields.gender",
      required: true,
      options: [
        { value: "MALE", label: "MALE" },
        { value: "FEMALE", label: "FEMALE" },
        { value: "OTHER", label: "OTHER" },
      ],
    },
  ],

  Ecuador: [
    {
      name: "url",
      type: "url",
      label: "fields.webhook_url",
      placeholder: "placeholders.webhook_url",
      required: true,
      maxLength: 255,
      helperText: "helpers.webhook_url",
    },
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
    },
    {
      name: "document_type",
      type: "select",
      label: "fields.document_type",
      required: true,
      options: [
        { value: "CEDULA", label: "CEDULA" },
        { value: "RUC", label: "RUC" },
        { value: "PASAPORTE", label: "PASAPORTE" },
      ],
    },
    {
      name: "document_number",
      type: "text",
      label: "fields.document_number",
      placeholder: "placeholders.document_number",
      required: true,
      helperText: "helpers.document_number_ecuador",
    },
    {
      name: "bank",
      type: "select",
      label: "fields.bank",
      required: true,
      options: BANKS_ECUADOR.map((bank) => ({ value: bank, label: bank })),
    },
    {
      name: "account_type",
      type: "select",
      label: "fields.account_type",
      required: true,
      options: [
        { value: "AHORROS", label: "AHORROS" },
        { value: "CORRIENTE", label: "CORRIENTE" },
      ],
    },
    {
      name: "account_number",
      type: "text",
      label: "fields.account_number",
      placeholder: "placeholders.account_number",
      required: true,
    },
  ],

  Panamá: [
    {
      name: "url",
      type: "url",
      label: "fields.webhook_url",
      placeholder: "placeholders.webhook_url",
      required: true,
      maxLength: 255,
      helperText: "helpers.webhook_url",
    },
    {
      name: "beneficiary",
      type: "text",
      label: "fields.recipient",
      placeholder: "placeholders.recipient",
      required: true,
    },
    {
      name: "identification_type",
      type: "select",
      label: "fields.identification_type",
      required: true,
      options: [
        { value: "CIP", label: "CIP" },
        { value: "PASAPORTE", label: "PASAPORTE" },
      ],
    },
    {
      name: "identification_number",
      type: "text",
      label: "fields.identification_number",
      placeholder: "placeholders.identification_number",
      required: true,
      helperText: "helpers.identification_number_panama",
    },
    {
      name: "bank",
      type: "select",
      label: "fields.bank",
      required: true,
      options: BANKS_PANAMA.map((bank) => ({ value: bank, label: bank })),
    },
    {
      name: "phone_number",
      type: "text",
      label: "fields.phone_number",
      placeholder: "placeholders.phone_number",
      required: true,
      pattern: /^\d{8}$/,
    },
  ],

  Perú: [
    {
      name: "url",
      type: "url",
      label: "fields.webhook_url",
      placeholder: "placeholders.webhook_url",
      required: true,
      maxLength: 255,
      helperText: "helpers.webhook_url",
    },
    {
      name: "first_name",
      type: "text",
      label: "fields.first_name",
      placeholder: "placeholders.first_name",
      required: true,
    },
    {
      name: "last_name",
      type: "text",
      label: "fields.last_name",
      placeholder: "placeholders.last_name",
      required: true,
    },
    {
      name: "identification_type",
      type: "text",
      label: "fields.identification_type",
      placeholder: "placeholders.identification_type",
      required: true,
    },
    {
      name: "identification_number",
      type: "text",
      label: "fields.identification_number",
      placeholder: "placeholders.identification_number",
      required: true,
    },
    {
      name: "bank",
      type: "select",
      label: "fields.bank",
      required: true,
      options: BANKS_PERU.map((bank) => ({ value: bank, label: bank })),
    },
    {
      name: "account_type",
      type: "select",
      label: "fields.account_type",
      required: true,
      options: [
        { value: "AHORROS", label: "AHORROS" },
        { value: "CORRIENTE", label: "CORRIENTE" },
        { value: "INTERBANCARIA", label: "INTERBANCARIA" },
      ],
    },
    {
      name: "account_number",
      type: "text",
      label: "fields.account_number",
      placeholder: "placeholders.account_number",
      required: true,
    },
    {
      name: "address",
      type: "text",
      label: "fields.address",
      placeholder: "placeholders.address",
      required: true,
    },
    {
      name: "city",
      type: "text",
      label: "fields.city",
      placeholder: "placeholders.city",
      required: true,
    },
    {
      name: "state",
      type: "text",
      label: "fields.state",
      placeholder: "placeholders.state",
      required: true,
    },
    {
      name: "postal_code",
      type: "text",
      label: "fields.postal_code",
      placeholder: "placeholders.postal_code",
      required: true,
    },
    {
      name: "birth_date",
      type: "date",
      label: "fields.birth_date",
      required: true,
    },
    {
      name: "email",
      type: "email",
      label: "fields.email",
      placeholder: "placeholders.email",
      required: true,
    },
    {
      name: "gender",
      type: "select",
      label: "fields.gender",
      required: true,
      options: [
        { value: "MALE", label: "MALE" },
        { value: "FEMALE", label: "FEMALE" },
        { value: "ORGANIZATION", label: "ORGANIZATION" },
      ],
    },
  ],

  Crypto: [
    {
      name: "first_name",
      type: "text",
      label: "fields.first_name",
      placeholder: "placeholders.first_name",
      required: true,
    },
    {
      name: "last_name",
      type: "text",
      label: "fields.last_name",
      placeholder: "placeholders.last_name",
      required: true,
    },
    {
      name: "identification_type",
      type: "text",
      label: "fields.identification_type",
      placeholder: "placeholders.identification_type",
      required: true,
    },
    {
      name: "identification_number",
      type: "text",
      label: "fields.identification_number",
      placeholder: "placeholders.identification_number",
      required: true,
    },
    {
      name: "email",
      type: "email",
      label: "fields.email",
      placeholder: "placeholders.email",
      required: true,
    },
    {
      name: "network",
      type: "select",
      label: "fields.network",
      required: true,
      options: [
        { value: "TRC20", label: "TRC20" },
        { value: "POLYGON", label: "POLYGON" },
      ],
    },
    {
      name: "wallet",
      type: "text",
      label: "fields.wallet",
      placeholder: "placeholders.wallet",
      required: true,
      helperText: "helpers.wallet_address",
    },
  ],

  Paraguay: [],
  Guatemala: [],
  "Unión Europea": [],
};

export function getCountryFieldConfig(country: CountryCode): FieldConfig[] {
  return COUNTRY_FIELD_CONFIGS[country] || [];
}
